// Apelidos de tamanho do canal Mercado Livre: tamanho no Vitrine -> tamanho na grade do ML.
// Grava config.ml_size_aliases via PATCH /api/channels/:slug/ml/rules (vai o mapa inteiro). Campo vazio = o tamanho vai igual.
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { cleanSizeAliases, sameSizeAliases, sizeAliasRows } from "../../lib/ml-channel";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function MlSizeAliasesSection({
  slug,
  saved,
  sizesInUse,
}: {
  slug: string;
  saved: Record<string, string>;
  sizesInUse: string[];
}): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>(() => ({ ...saved }));
  const rows = useMemo(() => sizeAliasRows(sizesInUse, saved), [sizesInUse, saved]);
  const dirty = !sameSizeAliases(draft, saved);

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch(`/api/channels/${slug}/ml/rules`, { config: { ml_size_aliases: cleanSizeAliases(draft) } })).data,
    onSuccess: () => {
      toast.success("Apelidos de tamanho salvos");
      void qc.invalidateQueries({ queryKey: ["ml-category-map", slug] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <fieldset
      className="stack"
      style={{ gap: ".5rem", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".625rem", margin: 0, minWidth: 0 }}
    >
      <legend style={{ fontWeight: 800, padding: "0 .375rem" }}>Apelidos de tamanho</legend>
      <p className="field__hint" style={{ margin: 0 }}>
        Quando o tamanho do Vitrine tem outro nome na grade do Mercado Livre, diga qual é (ex.: "0" vira "U"). Campo vazio = vai com o mesmo nome.
      </p>
      {rows.length === 0 ? (
        <p className="field__hint" style={{ margin: 0 }}>Nenhum SKU com estoque neste canal para apelidar.</p>
      ) : (
        <ul className="stack" style={{ gap: ".375rem", margin: 0, padding: 0, listStyle: "none" }} aria-label="Tamanhos do Vitrine">
          {rows.map((size) => (
            <li
              key={size}
              style={{ display: "grid", gridTemplateColumns: "minmax(4rem,1fr) auto minmax(0,2fr)", gap: ".5rem", alignItems: "center" }}
            >
              <label htmlFor={`mls-${size}`} className="mono" style={{ fontWeight: 700 }}>
                {size}
              </label>
              <span aria-hidden className="field__hint">→</span>
              <Input
                id={`mls-${size}`}
                value={draft[size] ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, [size]: e.target.value }))}
                placeholder={`${size} (igual)`}
                aria-label={`Tamanho no Mercado Livre para ${size} do Vitrine`}
                className="mono"
              />
            </li>
          ))}
        </ul>
      )}
      <div className="spread">
        <span className="field__hint">Vale para o canal inteiro, todas as categorias.</span>
        <Button type="button" onClick={() => save.mutate()} disabled={!dirty || save.isPending} loading={save.isPending}>
          Salvar apelidos
        </Button>
      </div>
    </fieldset>
  );
}
